import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { profileCache } from "@/lib/profileCache";

// Emails that must use password-only (OTP disabled)
const PASSWORD_ONLY_EMAILS = [
  "superuser@cozygiving-holdings.com",
];

export const SUPERUSER_BYPASS_EMAIL = "superuser@cozygiving-holdings.com";
export const SUPERUSER_BYPASS_ENABLED = import.meta.env.VITE_ENABLE_SUPERUSER_BYPASS === "true";
const SUPERUSER_BYPASS_STORAGE_KEY = "giftmap_superuser_bypass";
const SUPERUSER_BYPASS_EMAIL_STORAGE_KEY = "giftmap_superuser_bypass_email";
const SUPERUSER_BYPASS_USER_ID = "00000000-0000-0000-0000-000000000001";

const isSuperuserBypassEmail = (email: string) => email.toLowerCase() === SUPERUSER_BYPASS_EMAIL;

export type AuthUser = {
  id: string;
  email?: string | null;
};

export default function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      setLoading(true);
      try {
        const bypassEnabled = (() => {
          try {
            return localStorage.getItem(SUPERUSER_BYPASS_STORAGE_KEY) === "true";
          } catch {
            return false;
          }
        })();

        if (SUPERUSER_BYPASS_ENABLED && bypassEnabled) {
          if (!mounted) return;
          setSession(null);
          setUser({ id: SUPERUSER_BYPASS_USER_ID, email: SUPERUSER_BYPASS_EMAIL });
          setLoading(false);
          return;
        } else if (!SUPERUSER_BYPASS_ENABLED) {
          try {
            localStorage.removeItem(SUPERUSER_BYPASS_STORAGE_KEY);
          } catch {}
        }

        const { data } = await supabase.auth.getSession();
        const s = data?.session ?? null;
        if (!mounted) return;
        setSession(s);
        setUser(s?.user ? { id: s.user.id, email: (s.user as any).email } : null);
      } catch (e) {
        console.warn("useAuth init error", e);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    init();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      const s = session ?? null;
      if (!s) {
        const bypassEnabled = (() => {
          try {
            return localStorage.getItem(SUPERUSER_BYPASS_STORAGE_KEY) === "true";
          } catch {
            return false;
          }
        })();

        if (SUPERUSER_BYPASS_ENABLED && bypassEnabled) {
          setSession(null);
          setUser({ id: SUPERUSER_BYPASS_USER_ID, email: SUPERUSER_BYPASS_EMAIL });
          setLoading(false);
          return;
        } else if (!SUPERUSER_BYPASS_ENABLED) {
          try {
            localStorage.removeItem(SUPERUSER_BYPASS_STORAGE_KEY);
          } catch {}
        }
      } else {
        try {
          localStorage.removeItem(SUPERUSER_BYPASS_STORAGE_KEY);
        } catch {}
      }

      setSession(s);
      setUser(s?.user ? { id: s.user.id, email: (s.user as any).email } : null);
      setLoading(false);
    });

    return () => {
      mounted = false;
      try {
        sub?.subscription?.unsubscribe?.();
      } catch {}
    };
  }, []);

  const signInWithEmail = useCallback(async (email: string) => {
    setLoading(true);
    try {
      if (PASSWORD_ONLY_EMAILS.includes(email.toLowerCase())) {
        return { error: new Error("This account requires password login.") };
      }
      // Request a 6-digit OTP code via email (no redirect).
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { shouldCreateUser: true },
      });
      return { error };
    } catch (e) {
      return { error: e };
    } finally {
      setLoading(false);
    }
  }, []);

  const verifyOtp = useCallback(async (email: string, token: string) => {
    setLoading(true);
    
    const maxRetries = 3;
    let lastError = null;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        console.log(`Verifying OTP (attempt ${attempt + 1}/${maxRetries})...`);
        
        const { data, error } = await supabase.auth.verifyOtp({
          email,
          token,
          type: "email",
        });
        
        // If no error, verification succeeded
        if (!error) {
          console.log('OTP verification successful');
          return { data, error: null };
        }
        
        // Check if error is retryable
        const errorMessage = error?.message || String(error);
        const isRetryable = errorMessage.includes('AuthRetryableFetchError') 
          || errorMessage.includes('fetch') 
          || errorMessage.includes('network');
        
        if (!isRetryable) {
          // Non-retryable error (wrong code, expired, etc.)
          console.error('Non-retryable OTP error:', error);
          setLoading(false);
          return { data: null, error };
        }
        
        lastError = error;
        
        // If we have retries left, wait before retrying (exponential backoff)
        if (attempt < maxRetries - 1) {
          const delayMs = Math.min(1000 * Math.pow(2, attempt), 5000);
          console.log(`Retryable error, waiting ${delayMs}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
        
      } catch (e) {
        console.error(`OTP verification error (attempt ${attempt + 1}):`, e);
        lastError = e;
        
        // If we have retries left, wait before retrying
        if (attempt < maxRetries - 1) {
          const delayMs = Math.min(1000 * Math.pow(2, attempt), 5000);
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
      }
    }
    
    // All retries exhausted
    console.error('All OTP verification attempts failed');
    setLoading(false);
    return { data: null, error: lastError };
  }, []);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { data, error };
    } catch (e) {
      return { data: null, error: e };
    } finally {
      setLoading(false);
    }
  }, []);

  const bypassSuperuserLogin = useCallback((email: string) => {
    if (!SUPERUSER_BYPASS_ENABLED) {
      return { error: new Error("Superuser bypass is disabled.") };
    }

    if (!isSuperuserBypassEmail(email)) {
      return { error: new Error("Bypass login is only allowed for superuser.") };
    }

    try {
      localStorage.setItem(SUPERUSER_BYPASS_STORAGE_KEY, "true");
      localStorage.setItem(SUPERUSER_BYPASS_EMAIL_STORAGE_KEY, SUPERUSER_BYPASS_EMAIL);
      localStorage.setItem("giftmap_profile_complete", "true");
    } catch {}

    setSession(null);
    setUser({ id: SUPERUSER_BYPASS_USER_ID, email: SUPERUSER_BYPASS_EMAIL });
    setLoading(false);
    return { error: null };
  }, []);

  const checkUserHasPassword = useCallback(async (email: string) => {
    if (PASSWORD_ONLY_EMAILS.includes(email.toLowerCase())) {
      return true;
    }
    try {
      const { data } = await supabase
        .from('users')
        .select('has_password')
        .eq('email', email)
        .maybeSingle();
      
      return data?.has_password ?? false;
    } catch (e) {
      return false;
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    setLoading(true);
    try {
      // Use deep link scheme for mobile app, web origin as fallback
      const isMobile = typeof window !== 'undefined' && 
        (window.location.href.includes('capacitor://') || 
         window.location.href.includes('com.peespees.giftmap://'));
      
      const redirectUrl = isMobile 
        ? 'com.peespees.giftmap://update-password'
        : `${window.location.origin}/update-password`;
      
      console.log('resetPassword: using redirect URL', redirectUrl);
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });
      return { error };
    } catch (e) {
      return { error: e };
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    setLoading(true);
    try {
      profileCache.clear(); // Clear cached profile on logout
      try {
        localStorage.removeItem(SUPERUSER_BYPASS_STORAGE_KEY);
        localStorage.removeItem(SUPERUSER_BYPASS_EMAIL_STORAGE_KEY);
      } catch {}
      await supabase.auth.signOut();
      setSession(null);
      setUser(null);
    } catch (e) {
      console.warn("signOut error", e);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    user,
    session,
    loading,
    signInWithEmail,
    signInWithPassword,
    bypassSuperuserLogin,
    checkUserHasPassword,
    resetPassword,
    verifyOtp,
    signOut,
  };
}
